import axios from 'axios';
import { formatApiError } from './errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true,  // send HttpOnly cookies on every request
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Always attach a pre-formatted, human-readable string to every rejection.
        error.userMessage = formatApiError(error, null);

        // /auth/me returning 401 simply means "not logged in" — don't redirect.
        // /auth/login and /auth/register failing with 401/400 is a normal
        // rejected attempt, not a stale/expired session — let the calling
        // component's own catch block show the error, don't hard-redirect
        // (a redirect here wipes the toast before it can render).
        const url = error.config?.url || '';
        const isAuthCheck = url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthCheck) {
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export const auth = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    logout: () => api.post('/auth/logout'),
    me: () => api.get('/auth/me'),
    requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
    resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
    deleteAccount: () => api.delete('/auth/account'),
};

export const jobs = {
    create: (data) => api.post('/jobs', data),
    getAll: (sector) => api.get('/jobs', { params: { sector } }),
    getOne: (id) => api.get(`/jobs/${id}`),
    getMatches: (id) => api.get(`/jobs/${id}/matches`),
    update: (id, data) => api.put(`/jobs/${id}`, data),
    delete: (id) => api.delete(`/jobs/${id}`),
    getRoles: () => api.get('/roles'),
    generateDescription: (data) => api.post('/jobs/generate-description', data),
    estimateCost: (data) => api.post('/jobs/estimate-cost', data),
    getAlignment: (jobId, candidateId) => api.get(`/jobs/${jobId}/alignment/${candidateId}`),
    getAllAlignments: (jobId) => api.get(`/jobs/${jobId}/alignments`),
    recalculateAlignment: (jobId, candidateId) => api.post(`/jobs/${jobId}/alignment/${candidateId}/recalculate`),
};

export const shortlist = {
    add: (data) => api.post('/shortlist', data),
    remove: (id) => api.delete(`/shortlist/${id}`),
    getAll: (jobId) => api.get('/shortlist', { params: jobId ? { job_id: jobId } : {} }),
};

export const candidates = {
    updateProfile: (data) => api.put('/candidates/profile', data),
    getProfile: () => api.get('/candidates/profile'),
    getRecommended: () => api.get('/candidates/recommended-jobs')
};

export const trialsLegacy = {
    invite: (data) => api.post('/trials/invite', data),
    respond: (id, action) => api.put(`/trials/${id}/respond`, { action }),
    getAll: (status) => api.get('/trials', { params: { status } }),
};

export const messages = {
    send: (data) => api.post('/messages', data),
    getAll: () => api.get('/messages'),
    markRead: (id) => api.put(`/messages/${id}/read`)
};

export const admin = {
    getStats: () => api.get('/admin/stats'),
    getUsers: () => api.get('/admin/users'),
    getJobs: () => api.get('/admin/jobs'),
    getTrialBookings: () => api.get('/admin/trial-bookings'),
    getCandidateDetail: (candidateId) => api.get(`/admin/candidates/${candidateId}`),
    getOnboardingFunnel: (range) => api.get('/admin/onboarding/funnel', { params: { range } }),
    getOnboardingQuestions: (range) => api.get('/admin/onboarding/questions', { params: { range } }),
    getOnboardingCandidates: (range, stalled, sort) => api.get('/admin/onboarding/candidates', { params: { range, stalled, sort } }),
};

export const pricing = {
    getSector: (sector) => api.get(`/pricing/${sector}`),
    calculate: (sector, days) => api.post('/pricing/calculate', null, { params: { sector, days } })
};

export const assessments = {
    getSkillsList: () => api.get('/assessments/skills-list'),
    getMicroTest: (sector) => api.get(`/assessments/micro-test/${sector}`),
    getProblemSolvingQuestions: () => api.get('/assessments/questions/problem-solving'),
    getWorkingStyleQuestions: () => api.get('/assessments/questions/working-style'),
    submit: (data) => api.post('/assessments/submit', data),
    getMyAssessment: () => api.get('/assessments/my-assessment'),
    getStatus: () => api.get('/assessments/status'),
    getTalentCard: (candidateId) => api.get(`/assessments/talent-card/${candidateId}`),
    getFullAssessment: (candidateId) => api.get(`/assessments/candidate/${candidateId}`),
    getWildcards: (sector) => api.get('/assessments/wildcards', { params: { sector } }),
    getWildcardDescription: () => api.get('/assessments/wildcard-description')
};

// Trial & Payment APIs
export const trials = {
    book: (data) => api.post('/trials/book', data),
    getMyBookings: () => api.get('/trials/my-bookings'),
    candidateRespond: (bookingId, action) => api.put(`/trials/${bookingId}/candidate-respond`, { action }),
    candidateCancel: (bookingId, reason) => api.post(`/trials/${bookingId}/candidate-cancel`, { reason: reason || '' }),
    employerCancel: (bookingId, reason) => api.post(`/trials/${bookingId}/employer-cancel`, { reason: reason || '' }),
    requestEdit: (bookingId, changes, message) =>
        api.post(`/trials/${bookingId}/request-edit`, { changes: changes || {}, message: message || '' }),
    respondEdit: (bookingId, requestId, action, changes, message) =>
        api.post(`/trials/${bookingId}/respond-edit/${requestId}`, {
            action, changes: changes || {}, message: message || '',
        }),
    getDetails: (trialId) => api.get(`/trials/${trialId}`),
    start: (trialId) => api.post(`/trials/start?trial_id=${trialId}`),
    completeDay: (data) => api.post('/trials/complete-day', data),
    terminate: (data) => api.post('/trials/terminate', data),
    submitFeedback: (data) => api.post('/trials/feedback', data),
    getFeedback: (trialId) => api.get(`/trials/feedback/${trialId}`),
    makeOffer: (data) => api.post('/trials/make-offer', data),
    respondToOffer: (data) => api.post('/trials/respond-to-offer', data),
    getPendingOffers: () => api.get('/trials/offers/pending'),
    getCandidatePayouts: () => api.get('/trials/candidate-payouts'),
    markPayoutComplete: (trialId) => api.post(`/trials/mark-payout-complete?trial_id=${trialId}`)
};

export const payments = {
    getPricing: () => api.get('/payments/pricing'),
    calculate: (days) => api.post(`/payments/calculate?days=${days}`),
    createCheckout: (bookingId, originUrl, applyCredit = false) =>
        api.post(`/payments/create-checkout?booking_id=${bookingId}&origin_url=${encodeURIComponent(originUrl)}&apply_credit=${applyCredit ? 'true' : 'false'}`),
    getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
    createHiringFeeCheckout: (offerId, originUrl) => 
        api.post(`/payments/create-hiring-fee-checkout?offer_id=${offerId}&origin_url=${encodeURIComponent(originUrl)}`),
    getStripeKey: () => api.get('/stripe/publishable-key')
};

export const company = {
    getCreditBalance: () => api.get('/company/credit-balance'),
    resetDemoCredit: () => api.post('/company/reset-demo-credit'),
};

export const founding = {
    getStatus: () => api.get('/founding/status'),
    submit: (responses) => api.post('/founding/submit', responses),
    skip: () => api.post('/founding/skip')
};

// Fire-and-forget candidate onboarding funnel tracking (admin-only
// visibility). Never throws — a failed track call must never interrupt
// the candidate flow it's attached to, so callers can invoke this without
// awaiting or wrapping it in their own try/catch.
export const onboarding = {
    track: (step, questionNumber) =>
        api.post('/onboarding/track', { step, question_number: questionNumber ?? null }).catch(() => {})
};

export default api;